import React from "react"

import { setDeviceLocationPermanentAsync } from "@/base/farmhq-api"
import * as Geo from "@/base/geo"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { getActiveFieldsFromStateCached } from "@/redux/farm-fields.reducer"
import * as Models from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppIcons,
  AppText,
  Badge,
  Box,
  Button,
  Divider,
  Row,
  TitleWithIcon,
} from "@/theme"
import i18n from "@/translations/i18n"

import { ActionButtons } from "./ActionButtons"
import { Help } from "./Help"

import type { RootState } from "@/redux/root.reducer"
import type { AcceptsChildren, BoxProps } from "@/theme"

export function getIsLocationPinnedByDeviceId(
  state: RootState,
  deviceId: string,
): boolean {
  return Boolean(
    Models.deviceConfiguration.selectById(state, deviceId)?.locationPermanent,
  )
}
interface ProviderProps extends Models.DeviceIdProps {
  onClose: () => void
}

/**
 * Shared logic for permanent device location setting
 */
function useSetDeviceLocationPermanent({ deviceId, ...rest }: ProviderProps) {
  const { dispatch, handleError, isLoading, toasts } = useRequestHelpers(
    "SetDeviceLocationPermanent",
  )
  const [pinnedLocation, setPinnedLocation] = React.useState<
    Geo.MultiPoint | undefined
  >()

  const [isChanged, setIsChanged] = React.useState(false)
  const isLocationPinned = useRootSelector((state) =>
    Boolean(
      Models.deviceConfiguration.selectById(state, deviceId)
        ?.locationPermanent,
    ),
  )
  const onSubmit = (nextValue: Geo.PointGeoJson | null) => {
    dispatch(
      setDeviceLocationPermanentAsync({
        deviceId,
        locationPermanent: nextValue,
      }),
    )
      .unwrap()
      .then(() => setIsChanged(false))
      .then(() => setPinnedLocation(undefined))
      .then(() => toasts.success())

      .catch((error) =>
        handleError(error, {
          toastMessage: "default",
        }),
      )
  }

  const handleUnpin = () => onSubmit(null)
  const asGeoJson = Geo.point(pinnedLocation?.native)?.toJson()

  return {
    asGeoJson,
    fields: useRootSelector(getActiveFieldsFromStateCached),
    handleUnpin,
    isLoading,
    isLocationPinned,
    isSubmitDisabled: !isChanged || !Boolean(asGeoJson),
    onPlaceMarker: (coordinate: Geo.PointInput | undefined) => {
      const nextLocation = Geo.createMultiPoint(coordinate)
      if (nextLocation) {
        setIsChanged(true)
        setPinnedLocation(nextLocation)
      }
    },
    onSubmitPinnedLocation: () => {
      if (typeof asGeoJson === "undefined") {
        throw new TypeError("invalid location")
      }
      return onSubmit(asGeoJson)
    },
    pinnedLocation,
    ...rest,
  }
}

type ContextValue = ReturnType<typeof useSetDeviceLocationPermanent>

const Context = React.createContext<ContextValue | undefined>(undefined)

export function Provider({
  children,
  ...rest
}: AcceptsChildren & ProviderProps): React.JSX.Element | null {
  const value = useSetDeviceLocationPermanent(rest)
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useSetDeviceLocationPermanentContext(): ContextValue {
  const ctx = React.useContext(Context)
  if (typeof ctx === "undefined") {
    throw new TypeError(
      `Context must be used inside of SetDeviceLocationPermanentContextProvider`,
    )
  }
  return ctx
}

export function LocationPinnedBadge() {
  return (
    <Badge
      StartIconComponent={AppIcons.LocationPinned}
      colorScheme={"$success"}
    >
      {i18n.t("setDeviceLocationPermanent:locationPinned")}
    </Badge>
  )
}

export function LocationPinnedIndicator(props: BoxProps) {
  const { handleUnpin, isLoading, isLocationPinned } =
    useSetDeviceLocationPermanentContext()
  if (isLocationPinned) {
    // TODO: Make this look better
    return (
      <Row {...props} my={"$2"} space={"$1"}>
        <LocationPinnedBadge />
        <Button
          IconComponent={"TrashCan"}
          isDisabled={isLoading}
          // size="sm"
          text={i18n.t("setDeviceLocationPermanent:unpinButton")}
          variant={"text"}
          onPress={handleUnpin}
        />
      </Row>
    )
  }
  return (
    <Box {...props}>
      <Badge
        StartIconComponent={AppIcons.LocationUnpinned}
        colorScheme={"$gray"}
      >
        {i18n.t("setDeviceLocationPermanent:locationNotPinned")}
      </Badge>
    </Box>
  )
}

export function Controls() {
  const { isLoading, isSubmitDisabled, onClose, onSubmitPinnedLocation } =
    useSetDeviceLocationPermanentContext()
  return (
    <React.Fragment>
      <TitleWithIcon
        IconComponent={"Target"}
        headingVariant={"h5"}
        helpElement={<Help content={"setDeviceLocationPermanent"} />}
        titleText={i18n.t("setDeviceLocationPermanent:title")}
      />
      <Box mr={"auto"} mt={"$2"}>
        <LocationPinnedIndicator />
      </Box>
      <Divider my={"$2"} />
      <Box my={"$1"}>
        <AppText>
          {i18n.t("setDeviceLocationPermanent:tapTheMapToSetLocation")}
        </AppText>
      </Box>
      <ActionButtons
        cancelText={i18n.t("exit")}
        isDisabled={isLoading}
        isLoading={isLoading}
        isSubmitDisabled={isSubmitDisabled}
        mb={"$4"}
        mt={"$2"}
        orientation={"horizontal"}
        submitText={i18n.t("save")}
        onPressCancel={onClose}
        onPressSubmit={onSubmitPinnedLocation}
      />
    </React.Fragment>
  )
}
